import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import { object, string } from "yup";

import { CustomerFormData } from "@/components/screens/CustomerFormPage/types";
import { validateCNPJ, validateCPF } from "@/helpers/document-helpers";
import { base64ToBlob } from "@/helpers/file-helpers";
import ICustomer, { ICustomerInput } from "@/interfaces/customer.interfaces";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";
import { CustomerStatus } from "@/types/customer.types";
import { PersonType } from "@/types/document.types";

export const buildInitialData = (customer: ICustomer | null): CustomerFormData => {
  return {
    status: customer?.status || CustomerStatus.WaitingForEmailConfirmation,
    code: customer?.code || "",

    fantasy: customer?.fantasy || "",
    name: customer?.name || "",
    federalTaxId: customer?.federalTaxId || "",
    stateTaxId: customer?.stateTaxId || "",
    cityTaxId: customer?.cityTaxId || "",
    personType: customer?.fantasy ? PersonType.LegalEntity : PersonType.Individual,

    address: customer?.address || "",
    addressNumber: customer?.addressNumber || "",
    addressComp: customer?.addressComp || "",
    district: customer?.district || "",
    city: customer?.city || "",
    state: customer?.state || "",
    country: customer?.country || "BRASIL",
    stateUf: customer?.stateUf || "",
    zipCode: customer?.zipCode || "",

    phone1: customer?.phone1 ?? "",
    phone2: customer?.phone2 ?? "",
    mobilePhone: customer?.mobilePhone ?? "",
    email: customer?.email || "",
    nameUser: "",
    image: customer?.image || "",
    paymentKey: customer?.paymentKey || "",
    customerDescription: customer?.customerDescription || "",
    birthDate: customer?.birthDate || null,
  };
};

export const buildFormSchema = (messages: CustomerFormMessages) => {
  return object().shape({
    status: string().required(messages.errorRequiredStatus),
    address: string().required(messages.errorRequiredAddress),
    addressNumber: string().required(messages.errorRequiredAddressNumber),
    district: string().required(messages.errorRequiredDistrict),
    city: string().required(messages.errorRequiredCity),
    state: string().required(messages.errorRequiredState),
    country: string().required(messages.errorRequiredCountry),
    zipCode: string().required(messages.errorRequiredZipCode),

    mobilePhone: string()
      .test("is-valid-phone", messages.errorInvalidPhone, (value) => {
        const trimmedMobilePhone = (value ?? "").trim();
        if (trimmedMobilePhone === "") return true;
        const parsedBrazilPhone = parsePhoneNumberFromString(trimmedMobilePhone, "BR");
        return parsedBrazilPhone ? parsedBrazilPhone.isValid() : false;
      })
      .notRequired(),

    phone1: string()
      .test("is-valid-phone", messages.errorInvalidPhone, (value) => {
        const trimmedLandlinePhone = (value ?? "").trim();
        if (trimmedLandlinePhone === "") return true;
        const parsedBrazilPhone = parsePhoneNumberFromString(trimmedLandlinePhone, "BR");
        return parsedBrazilPhone ? parsedBrazilPhone.isValid() : false;
      })
      .notRequired(),

    name: string().required(messages.errorRequiredName),

    fantasy: string().when("personType", {
      is: PersonType.LegalEntity,
      then: (schema) => schema.required(messages.errorRequiredFantasy),
      otherwise: (schema) => schema.notRequired(),
    }),

    federalTaxId: string().when("personType", {
      is: PersonType.Individual,
      then: (schema) =>
        schema.test("cpf", messages.invalidCPF, (cpf?: string) => validateCPF(cpf || "")),
      otherwise: (schema) =>
        schema.test("cnpj", messages.invalidCNPJ, (cnpj?: string) => validateCNPJ(cnpj || "")),
    }),
    customerDescription: string().optional(),
  });
};

export const normalizeUpdateData = (formData: CustomerFormData): FormData => {
  const dataToSubmit = new FormData();

  const cleanTaxId = (taxId: string) => taxId.replace(/[^\d]/g, "");

  dataToSubmit.append("status", formData.status);
  dataToSubmit.append("code", formData.code);
  dataToSubmit.append("fantasy", formData.fantasy);
  dataToSubmit.append("name", formData.name);
  dataToSubmit.append("federalTaxId", cleanTaxId(formData.federalTaxId));
  dataToSubmit.append("stateTaxId", formData.stateTaxId);
  dataToSubmit.append("cityTaxId", formData.cityTaxId);
  dataToSubmit.append("address", formData.address);
  dataToSubmit.append("addressNumber", formData.addressNumber);
  dataToSubmit.append("addressComp", formData.addressComp);
  dataToSubmit.append("district", formData.district);
  dataToSubmit.append("city", formData.city);
  dataToSubmit.append("state", formData.state);
  dataToSubmit.append("stateUf", formData.stateUf ?? "");
  dataToSubmit.append("country", formData.country);
  dataToSubmit.append("zipCode", formData.zipCode);
  dataToSubmit.append("phone1", formData.phone1);
  dataToSubmit.append("phone2", formData.phone2);
  dataToSubmit.append("mobilePhone", formData.mobilePhone);
  dataToSubmit.append("email", formData.email);
  dataToSubmit.append("paymentKey", formData.paymentKey || "");
  dataToSubmit.append("customerDescription", formData.customerDescription || "");

  if (typeof formData.image === "string" && formData.image !== "") {
    const [, extension] = formData.image.match(/data:image\/(\w+);base64,/) || [];
    const base64String = `data:image/png;base64,${formData.image}`;
    const blob = base64ToBlob(base64String, `image/${extension || "png"}`);
    const fileName = `imagem.${extension || "png"}`;
    const file = new File([blob], fileName, { type: `image/${extension || "png"}` });
    dataToSubmit.append("file", file);
  } else if (formData.image instanceof File) {
    dataToSubmit.append("file", formData.image);
  } else {
    dataToSubmit.append("file", "");
  }

  return dataToSubmit;
};

export const normalizeCreateData = (formData: CustomerFormData): ICustomerInput => {
  const cleanTaxId = (taxId: string) => taxId.replace(/[^\d]/g, "");

  return {
    status: formData.status,
    code: formData.code,
    fantasy: formData.fantasy,
    name: formData.name,
    federalTaxId: cleanTaxId(formData.federalTaxId),
    stateTaxId: formData.stateTaxId,
    cityTaxId: formData.cityTaxId,
    address: formData.address,
    addressNumber: formData.addressNumber,
    addressComp: formData.addressComp,
    district: formData.district,
    city: formData.city,
    state: formData.state,
    stateUf: formData.stateUf ?? "",
    country: formData.country,
    zipCode: formData.zipCode,
    phone1: formData.phone1,
    phone2: formData.phone2,
    mobilePhone: formData.mobilePhone,
    email: formData.email,
    paymentKey: formData.paymentKey || "",
    customerDescription: formData.customerDescription || "",
  };
};
